import { assertValue } from '@shared/helpers'
import {
  IfpCompanyForm,
  IfpEmploymentType,
  IfpOccupation,
  IfpWorkingHours,
  SecLoanCarOwnershipType,
  SecLoanChildrenAgeGroup,
  SecLoanOtherIncomeType,
  SHORT_TEXT_INPUT_MAX_LENGTH,
  TimeUntilEmployedAgainEstimation,
} from '@shared/sec-loans'
import { MAX_REASONABLE_NUMBER, MIN_REASONABLE_NUMBER } from '@shared/validation'
import { YesNo } from '@ui-common/types'
import {
  MINIMUM_REASONABLE_YEAR,
  testIsValidMonetaryValue,
  toNumber,
  validateBusinessId,
  validateFinnishMobilePhoneNumberInput,
  validateFutureMonthAndYear,
  validatePastMonthAndYear,
  validateShortTextInput,
} from '@ui-common/utils'
import { FieldName } from 'react-hook-form'
import { IntlShape } from 'react-intl'
import { create, enforce, omitWhen, only, Suite, test } from 'vest'

import { ApplicantInfoFormData as FormData } from './types'

export const createFormValidator = ({
  isShortFlow,
  isAsyncValidationEnabled,
  formatMessage,
}: {
  isShortFlow: boolean
  isAsyncValidationEnabled: boolean
  formatMessage: IntlShape['formatMessage']
}): Suite<string, string, (data: FormData, changedFields?: FieldName<FormData>[]) => void> => {
  return create((data, changedFields) => {
    only(changedFields)

    validateFinnishMobilePhoneNumberInput({
      fieldName: 'phoneNumber',
      phoneNumber: data.phoneNumber,
      isAsyncValidationEnabled,
      formatMessage,
    })

    omitWhen(isShortFlow, () => {
      validateLongWizardFields(data, formatMessage)
    })
  })
}

const validateCommonEmploymentFields = ({
  formData,
  formatMessage,
}: {
  formData: FormData
  formatMessage: IntlShape['formatMessage']
}) => {
  test('employmentType', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(formData.employmentType).inside(Object.values(IfpEmploymentType))
  })

  validateShortTextInput('employer', formatMessage, formData.employer, SHORT_TEXT_INPUT_MAX_LENGTH, true)

  test('workingHours', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(formData.workingHours).inside(Object.values(IfpWorkingHours))
  })

  validateShortTextInput('profession', formatMessage, formData.profession, SHORT_TEXT_INPUT_MAX_LENGTH, true)

  validatePastMonthAndYear({
    fieldName: 'employmentStartDate',
    dateToValidate: formData.employmentStartDate,
    formatMessage,
    minimumYear: MINIMUM_REASONABLE_YEAR,
    isRequired: true,
  })

  omitWhen(formData.employmentType !== IfpEmploymentType.TimeLimited, () => {
    validateFutureMonthAndYear({
      fieldName: 'employmentEndDate',
      dateToValidate: formData.employmentEndDate,
      formatMessage,
      isRequired: true,
    })
  })
}

const validateLongWizardFields = (data: FormData, formatMessage: IntlShape['formatMessage']) => {
  test('occupation', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(data.occupation).inside(Object.values(IfpOccupation))
  })

  omitWhen(data.occupation !== IfpOccupation.Pensioner, () => {
    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'grossSalary',
      incomeAfterTaxesField: 'netSalary',
      beforeTaxesLabelKey: 'sec-loans-income-info-gross-salary-for-pensioners-form-label',
      afterTaxesLabelKey: 'sec-loans-income-info-net-salary-for-pensioners-form-label',
      minValue: 0,
    })
  })

  omitWhen(data.occupation !== IfpOccupation.Employed, () => {
    validateCommonEmploymentFields({
      formatMessage,
      formData: data,
    })

    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'grossSalary',
      incomeAfterTaxesField: 'netSalary',
      beforeTaxesLabelKey: 'sec-loans-income-info-gross-salary-form-label',
      afterTaxesLabelKey: 'sec-loans-income-info-net-salary-form-label',
      minValue: 0,
    })
  })

  omitWhen(data.occupation !== IfpOccupation.Furlough, () => {
    validateFutureMonthAndYear({
      fieldName: 'estimatedFurloughEndDate',
      dateToValidate: data.estimatedFurloughEndDate,
      formatMessage,
      isRequired: true,
    })

    validateCommonEmploymentFields({
      formData: data,
      formatMessage,
    })

    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
      incomeAfterTaxesField: 'currentIncomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      minValue: 0,
    })

    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'normalGrossSalary',
      incomeAfterTaxesField: 'normalNetSalary',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
      minValue: 0,
    })
  })

  omitWhen(data.occupation !== IfpOccupation.Entrepreneur, () => {
    validateShortTextInput('companyName', formatMessage, data.companyName, SHORT_TEXT_INPUT_MAX_LENGTH, true)
    validateBusinessId({ fieldName: 'businessId', formatMessage, idToValidate: data.businessId, isRequired: true })

    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'incomeBeforeTaxes',
      incomeAfterTaxesField: 'incomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-income-after-taxes-label',
      minValue: 0,
    })

    test('legalFormOfTheCompany', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.legalFormOfTheCompany).inside(Object.values(IfpCompanyForm))
    })

    validatePastMonthAndYear({
      fieldName: 'companyFoundingDate',
      dateToValidate: data.companyFoundingDate,
      formatMessage,
      minimumYear: MINIMUM_REASONABLE_YEAR,
      isRequired: true,
    })
  })

  omitWhen(data.occupation !== IfpOccupation.LongtermSickLeave, () => {
    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
      incomeAfterTaxesField: 'currentIncomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      minValue: 0,
    })

    test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
      validateFutureMonthAndYear({
        fieldName: 'estimatedReturnToWorkDate',
        dateToValidate: data.estimatedReturnToWorkDate,
        formatMessage,
        isRequired: true,
      })

      validateCommonEmploymentFields({
        formData: data,
        formatMessage,
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'normalGrossSalary',
        incomeAfterTaxesField: 'normalNetSalary',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        minValue: 0,
      })
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
      test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
      })

      omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
        test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
        })
      })
    })
  })

  omitWhen(data.occupation !== IfpOccupation.Unemployed, () => {
    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
      incomeAfterTaxesField: 'currentIncomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      minValue: 0,
    })

    test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
    })

    omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
      test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
      })
    })
  })

  omitWhen(data.occupation !== IfpOccupation.OnFamilyLeave, () => {
    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
      incomeAfterTaxesField: 'currentIncomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      minValue: 0,
    })

    test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
      validateFutureMonthAndYear({
        fieldName: 'estimatedReturnToWorkDate',
        dateToValidate: data.estimatedReturnToWorkDate,
        formatMessage,
        isRequired: true,
      })

      validateCommonEmploymentFields({
        formData: data,
        formatMessage,
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'normalGrossSalary',
        incomeAfterTaxesField: 'normalNetSalary',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        minValue: 0,
      })
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
      test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
      })

      omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
        test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
        })
      })
    })
  })

  omitWhen(data.occupation !== IfpOccupation.StudyLeave, () => {
    validateIncomeFields({
      formatMessage,
      formData: data,
      incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
      incomeAfterTaxesField: 'currentIncomeAfterTaxes',
      beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
      afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      minValue: 0,
    })

    test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
      validateFutureMonthAndYear({
        fieldName: 'estimatedReturnToWorkDate',
        dateToValidate: data.estimatedReturnToWorkDate,
        formatMessage,
        isRequired: true,
      })

      validateCommonEmploymentFields({
        formData: data,
        formatMessage,
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'normalGrossSalary',
        incomeAfterTaxesField: 'normalNetSalary',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        minValue: 0,
      })
    })

    omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
      test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
      })

      omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
        test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
        })
      })
    })
  })

  test('hasOtherIncome', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(data.hasOtherIncome).inside(Object.values(YesNo))
  })

  omitWhen(data.hasOtherIncome !== YesNo.Yes, () => {
    data.otherIncomes?.forEach((otherIncome, index) => {
      test(`otherIncomes.${index}.incomeType`, formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(otherIncome.incomeType).inside(Object.values(SecLoanOtherIncomeType))
      })

      testIsValidMonetaryValue(
        `otherIncomes.${index}.amountAfterTaxes`,
        otherIncome.amountAfterTaxes,
        formatMessage,
        MAX_REASONABLE_NUMBER,
        true,
        MIN_REASONABLE_NUMBER,
      )
    })
  })

  testIsValidMonetaryValue('totalSavings', data.totalSavings, formatMessage, MAX_REASONABLE_NUMBER, true, 0)

  testIsValidMonetaryValue(
    'ownShareOfHousingExpenses',
    data.ownShareOfHousingExpenses,
    formatMessage,
    MAX_REASONABLE_NUMBER,
    true,
    0,
  )

  test('hasCarOwnership', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(data.hasCarOwnership).inside(Object.values(YesNo))
  })

  omitWhen(data.hasCarOwnership !== YesNo.Yes, () => {
    test('carOwnershipType', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.carOwnershipType).inside(Object.values(SecLoanCarOwnershipType))
    })
  })

  test('isPayingChildSupport', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(data.isPayingChildSupport).inside(Object.values(YesNo))
  })

  omitWhen(data.isPayingChildSupport !== YesNo.Yes, () => {
    data.childSupports?.forEach((childSupport, index) => {
      test(`childSupports.${index}.ageGroup`, formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(childSupport.ageGroup).inside(Object.values(SecLoanChildrenAgeGroup))
      })
      testIsValidMonetaryValue(`childSupports.${index}.supportAmount`, childSupport.supportAmount, formatMessage)
    })
  })

  testIsValidMonetaryValue('totalLoansAmount', data.totalLoansAmount, formatMessage, MAX_REASONABLE_NUMBER, true, 0)

  testIsValidMonetaryValue(
    'totalAmountOfLoanRepaymentsPerMonth',
    data.totalAmountOfLoanRepaymentsPerMonth,
    formatMessage,
    MAX_REASONABLE_NUMBER,
    true,
    0,
  )

  test('payingHousingCompanyLoan', formatMessage({ id: 'validate-required-field-missing' }), () => {
    enforce(data.payingHousingCompanyLoan).inside(Object.values(YesNo))
  })

  omitWhen(data.payingHousingCompanyLoan !== YesNo.Yes, () => {
    testIsValidMonetaryValue('totalHousingCompanyLoan', data.totalHousingCompanyLoan, formatMessage)
  })
}

const validateIncomeFields = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, any>,
  K1 extends Extract<keyof T, string>,
  K2 extends Extract<keyof T, string>,
>({
  formData,
  incomeBeforeTaxesField,
  incomeAfterTaxesField,
  beforeTaxesLabelKey,
  afterTaxesLabelKey,
  formatMessage,
  minValue,
}: {
  formData: T
  incomeBeforeTaxesField: K1
  incomeAfterTaxesField: K2
  beforeTaxesLabelKey: string
  afterTaxesLabelKey: string
  formatMessage: IntlShape['formatMessage']
  minValue: number
}) => {
  const incomeBeforeTaxesValue = formData[incomeBeforeTaxesField]
  const incomeAfterTaxesValue = formData[incomeAfterTaxesField]
  testIsValidMonetaryValue(
    incomeBeforeTaxesField,
    incomeBeforeTaxesValue,
    formatMessage,
    MAX_REASONABLE_NUMBER,
    true,
    minValue,
  )
  testIsValidMonetaryValue(
    incomeAfterTaxesField,
    incomeAfterTaxesValue,
    formatMessage,
    MAX_REASONABLE_NUMBER,
    true,
    minValue,
  )

  omitWhen(!incomeBeforeTaxesValue || !incomeAfterTaxesValue, () => {
    test(
      incomeAfterTaxesField,
      formatMessage(
        { id: 'sec-loans-validate-value-is-not-more-than-other-value' },
        {
          firstValue: formatMessage({ id: beforeTaxesLabelKey }),
          secondValue: formatMessage({ id: afterTaxesLabelKey }),
        },
      ),
      () => {
        const incomeAfterTaxes = assertValue(toNumber(incomeAfterTaxesValue), 'incomeAfterTaxesValue')
        const incomeBeforeTaxes = assertValue(toNumber(incomeBeforeTaxesValue), 'incomeBeforeTaxesValue')
        enforce(incomeBeforeTaxes >= incomeAfterTaxes).isTruthy()
      },
    )
  })
}
